import { ContentPaste } from "@mui/icons-material"
import { Stack, Tooltip, Typography } from "@mui/material"
import { noop } from "lodash"
import React, { useCallback } from "react"
import { useCopyToClipboard } from "usehooks-ts"

interface TraceTooltipProps {
  text?: string
  tooltipTitle?: string | React.ReactNode
  onCopy?: () => void
  sx?: object
}

const TraceTooltip: React.FC<TraceTooltipProps> = ({
  text,
  tooltipTitle = "Copy to clipboard",
  onCopy = noop,
  sx = {},
}) => {
  const [showToolip, setShowTooltip] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const [copiedText, copy] = useCopyToClipboard()

  React.useEffect(() => {
    const updateTooltipState = () => {
      const traceTooltip = localStorage.getItem("traceTooltip")
      setShowTooltip(traceTooltip === "true")
    }

    // Set initial state
    updateTooltipState()

    // Listen for localStorage changes
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "traceTooltip") {
        updateTooltipState()
      }
    }

    window.addEventListener("storage", handleStorageChange)

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [])

  const onCopyHandler = useCallback(() => {
    if (text) {
      copy(text)
        .then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1000)
          if (onCopy) {
            onCopy()
          }
        })
        .catch((error: Error) => {
          console.error("Failed to copy!", error)
        })
    }
  }, [text, onCopy, copy])

  return (
    <Tooltip
      open={showToolip}
      title={
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={onCopyHandler}
        >
          <ContentPaste fontSize="small" />
          <Typography
            variant="caption"
            sx={{
              cursor: "pointer",
              whiteSpace: "nowrap",
              ...sx,
            }}
          >
            {text}
          </Typography>
          {copied && (
            <Typography variant="caption" color="success">
              Copied!
            </Typography>
          )}
        </Stack>
      }
    >
      <span>{tooltipTitle}</span>
    </Tooltip>
  )
}

export default TraceTooltip
